import { CircleCheck, CircleDashed, Clock } from 'lucide-react';
import { RenderIf } from 'react-rainbow-components';
import { InvoiceAcceptance, InvoiceSignatureState } from '../types';
import {
    formatAcceptedAt,
    signatureImageUrl,
    signatureMethodLabel,
} from '../helpers';
import {
    Container,
    Mark,
    Note,
    SignatureImage,
    StatusCard,
    StatusIcon,
    StatusMeta,
    StatusText,
    StatusTitle,
    Title,
    TypedName,
} from './styled';

interface SignatureDetailsProps {
    state: InvoiceSignatureState;
    acceptance?: InvoiceAcceptance | null;
    locale?: string;
    showTitle?: boolean;
    className?: string;
}

const SignatureDetails = ({
    state,
    acceptance,
    locale = 'en-US',
    showTitle = true,
    className,
}: SignatureDetailsProps) => {
    const isSigned = state === 'signed' && Boolean(acceptance);
    const imageUrl = signatureImageUrl(acceptance);
    const method = signatureMethodLabel(acceptance);
    const acceptedOn = acceptance ? formatAcceptedAt(acceptance.acceptedAt, locale) : '';
    const name = acceptance?.name?.trim() || '';
    const signedMeta = [method, acceptedOn && `Accepted on ${acceptedOn}`]
        .filter(Boolean)
        .join(' · ');
    const isUnrequested = !isSigned && state === 'unrequested';
    let icon = <Clock />;
    let title = 'Awaiting signature';
    let description = 'The customer has not signed this invoice yet.';
    if (isSigned) {
        icon = <CircleCheck />;
        title = 'Invoice accepted';
    } else if (isUnrequested) {
        icon = <CircleDashed />;
        title = 'Signature not requested';
        description = 'No signature link has been created for this invoice yet.';
    }

    return (
        <Container className={className}>
            <RenderIf isTrue={showTitle}>
                <Title>Customer acceptance</Title>
            </RenderIf>
            <StatusCard $signed={isSigned}>
                <StatusIcon>{icon}</StatusIcon>
                <StatusText>
                    <StatusTitle>{title}</StatusTitle>
                    <StatusMeta $signed={isSigned}>
                        {isSigned ? signedMeta : description}
                    </StatusMeta>
                </StatusText>
            </StatusCard>
            <RenderIf isTrue={isSigned}>
                <RenderIf isTrue={imageUrl || name}>
                    <Mark>
                        {imageUrl ? (
                            <SignatureImage
                                src={imageUrl}
                                alt={name ? `Signature of ${name}` : 'Customer signature'}
                            />
                        ) : <TypedName>{name}</TypedName>}
                    </Mark>
                </RenderIf>
                <Note>The signature is included in the signed PDF.</Note>
            </RenderIf>
        </Container>
    );
};

export default SignatureDetails;
